import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle, XCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const PORTAL_TYPES = ["Career Portal", "Easy Apply", "Marketplace", "Networking", "Other"];

const EMPTY = { name: "", url: "", portal_type: "Career Portal", sort_order: 0, active: true, notes: "" };

export default function JobPortalsAdmin() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showInactive, setShowInactive] = useState(true);

  const load = useCallback(async () => {
    const { data } = await api.get("/job-portals", { params: { include_inactive: showInactive } });
    setRows(data);
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/job-portals", { ...form, sort_order: rows.length });
      toast.success("Portal added");
      setForm(EMPTY);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const toggleActive = async (row) => {
    try { await api.put(`/job-portals/${row.id}`, { active: !row.active }); load(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Deactivate portal "${row.name}"?`)) return;
    try { await api.delete(`/job-portals/${row.id}`); load(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="job-portals-admin">
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-2 bg-slate-50 border border-slate-200 rounded-md p-3">
        <input required placeholder="Portal name (e.g. Workday)" value={form.name}
               onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
               data-testid="jp-name" className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
        <input placeholder="URL (optional)" value={form.url}
               onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
               data-testid="jp-url" className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
        <select value={form.portal_type}
                onChange={(e) => setForm((f) => ({ ...f, portal_type: e.target.value }))}
                data-testid="jp-type" className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          {PORTAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input placeholder="Notes" value={form.notes}
               onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
               className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
        <button type="submit" data-testid="jp-add" className="bg-sky-600 hover:bg-sky-700 text-white text-sm rounded-md px-4 py-2 inline-flex items-center justify-center gap-1">
          <Plus size={14} /> Add portal
        </button>
      </form>

      <label className="inline-flex items-center gap-2 text-xs text-slate-500">
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="jp-show-inactive" />
        Show inactive portals
      </label>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-2">Portal</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">URL</th>
              <th className="text-left px-4 py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-6">No portals yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium">{r.name}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{r.portal_type}</td>
                <td className="px-4 py-2 text-xs">
                  {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">{r.url}</a> : "—"}
                </td>
                <td className="px-4 py-2">
                  <button onClick={() => toggleActive(r)} data-testid={`jp-toggle-${r.id}`}
                          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {r.active ? <CheckCircle size={12} /> : <XCircle size={12} />}
                    {r.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => remove(r)} className="text-red-600 hover:text-red-800 p-1" data-testid={`jp-remove-${r.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
